import React, { useState } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import type { DashboardMealPlanActionItem, DashboardTodayResponse } from '@/lib/api/dashboard';

import { DashboardTodayKpiRow } from './DashboardTodayKpiRow';
import { DashboardUpcomingScheduleCard } from './DashboardUpcomingScheduleCard';
import { DashboardMealPlansCard } from './DashboardMealPlansCard';
import { DashboardInvoicesBillingCard } from './DashboardInvoicesBillingCard';
import { DashboardGettingStartedPanel } from './DashboardGettingStartedPanel';

export interface DashboardTodayTabProps {
  data?: DashboardTodayResponse;
  isLoading?: boolean;
  isPending?: boolean;
  error?: string;
  showGettingStarted?: boolean;
  onAddFirstClient: () => void;
  onSetAvailability: () => void;
  onSetupBilling: () => void;
  onViewCalendar: () => void;
  onViewAllAppointments: () => void;
  onViewAllMealPlans: () => void;
  onReassignMealPlan?: (row: DashboardMealPlanActionItem) => void;
  onViewAllInvoices: () => void;
  onInvoicesChanged?: () => void | Promise<void>;
}

export const DashboardTodayTab: React.FC<DashboardTodayTabProps> = ({
  data,
  isLoading = false,
  isPending = false,
  error,
  showGettingStarted = false,
  onAddFirstClient,
  onSetAvailability,
  onSetupBilling,
  onViewCalendar,
  onViewAllAppointments,
  onViewAllMealPlans,
  onReassignMealPlan,
  onViewAllInvoices,
  onInvoicesChanged,
}) => {
  const [scheduleExpanded, setScheduleExpanded] = useState(false);
  const [mealPlansExpanded, setMealPlansExpanded] = useState(false);
  const [invoicesExpanded, setInvoicesExpanded] = useState(true);
  const statusMessage = error || (isPending ? 'Dashboard stats are being prepared. Please check again shortly.' : undefined);

  return (
    <Stack spacing={1.75}>
      {statusMessage && (
        <Paper
          elevation={0}
          sx={{ border: '1px solid #e5e7eb', borderRadius: '12px', px: 2, py: 1.25, bgcolor: '#fbfcff' }}
        >
          <Typography sx={{ fontSize: 12, color: '#4a5565' }}>{statusMessage}</Typography>
        </Paper>
      )}
      {showGettingStarted && (
        <DashboardGettingStartedPanel
          onAddClient={onAddFirstClient}
          onSetAvailability={onSetAvailability}
          onSetupBilling={onSetupBilling}
        />
      )}
      <DashboardTodayKpiRow data={data} isLoading={isLoading} />
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.75} useFlexGap alignItems="flex-start">
        <Stack sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}>
          <DashboardUpcomingScheduleCard
            items={data?.appointments.items}
            appointmentsTodayCount={data?.cards.appointmentsToday.count}
            isLoading={isLoading}
            expanded={scheduleExpanded}
            onToggleExpanded={() => setScheduleExpanded((v) => !v)}
            onViewAll={onViewAllAppointments}
            onViewCalendar={onViewCalendar}
          />
        </Stack>
        <Stack sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}>
          <DashboardMealPlansCard
            items={data?.mealPlansToAction.items}
            isLoading={isLoading}
            expanded={mealPlansExpanded}
            onToggleExpanded={() => setMealPlansExpanded((v) => !v)}
            onViewAll={onViewAllMealPlans}
            onReassignPlan={onReassignMealPlan}
          />
        </Stack>
      </Stack>
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', lg: (theme) => `calc((100% - ${theme.spacing(1.75)}) / 2)` },
        }}
      >
        <DashboardInvoicesBillingCard
          items={data?.invoicesAndBilling.items}
          isLoading={isLoading}
          expanded={invoicesExpanded}
          onToggleExpanded={() => setInvoicesExpanded((v) => !v)}
          onViewAll={onViewAllInvoices}
          onInvoiceDataChanged={onInvoicesChanged}
        />
      </Box>
    </Stack>
  );
};
